import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import {
  Search, SlidersHorizontal, BookOpen, Sparkles, Filter,
  Trash2, Edit3, Plus, ArrowLeft, Eye, Heart, HelpCircle, Lock, LogOut, Loader2
} from 'lucide-react';

import { authService, blogService } from '../services/api';
import BlogCard from '../components/Blog/BlogCard';
import BlogEditor from '../components/Blog/BlogEditor';
import AuthModal from '../components/Blog/AuthModal';
import SEO from '../components/SEO/SEO';
import { SITE_URL } from '../config/site';

// Cached across route changes so leaving and returning to /blogs is instant.
// Refreshed in the background on each mount (stale-while-revalidate) and after
// any create/update/delete. Lives for the SPA session (cleared on full reload).
let cachedBlogs = null;

export default function Blogs() {
  const [blogs, setBlogs] = useState(cachedBlogs || []);
  const [isLoading, setIsLoading] = useState(!cachedBlogs);
  const [busyPostId, setBusyPostId] = useState(null); // card whose edit/delete is in-flight
  const [isAdmin, setIsAdmin] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const [activeView, setActiveView] = useState('blog-list');
  const [editingPost, setEditingPost] = useState(null);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTag, setSelectedTag] = useState('All');
  const [sortBy, setSortBy] = useState('newest');

  useEffect(() => {
    fetchBlogs();
    checkAuth();
  }, []);

  // Edit coming straight from an article page: the full post (with content) is
  // handed over in router state, so open the editor immediately — no list fetch,
  // no extra getBySlug round trip.
  useEffect(() => {
    const post = location.state?.editPost;
    if (post) {
      setEditingPost(post);
      setActiveView('editor');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      navigate('/blogs', { replace: true, state: {} }); // clear so back-nav doesn't re-open
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  // List posts omit `content` (kept out of the list payload for speed), so the
  // editor loads the full article by slug before opening.
  const openEditor = async (post) => {
    setBusyPostId(post._id);
    try {
      const res = await blogService.getBySlug(post.slug);
      setEditingPost(res.success ? res.data : post);
    } catch (error) {
      setEditingPost(post);
    } finally {
      setBusyPostId(null);
    }
    setActiveView('editor');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const fetchBlogs = async () => {
    try {
      const res = await blogService.getAll();
      if (res.success) {
        cachedBlogs = res.data;
        setBlogs(res.data);
      }
    } catch (error) {
      console.error('Error fetching blogs', error);
    } finally {
      setIsLoading(false);
    }
  };

  const checkAuth = async () => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const res = await authService.verify();
        if (res.success) setIsAdmin(true);
      } catch (error) {
        authService.logout();
      }
    }
  };

  const allDynamicTags = ['All', ...Array.from(new Set(blogs.flatMap(b => b.tags || [])))];

  const handleLoginToggle = () => {
    if (isAdmin) {
      authService.logout();
      setIsAdmin(false);
      if (activeView === 'editor') {
        setActiveView('blog-list');
      }
    } else {
      setIsAuthModalOpen(true);
    }
  };

  const handleLoginSuccess = () => {
    setIsAdmin(true);
  };

  const handleNewPost = () => {
    setEditingPost(null);
    setActiveView('editor');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleEditPost = (post) => openEditor(post);

  const handleDeletePost = async (id) => {
    setBusyPostId(id);
    try {
      await blogService.delete(id);
      await fetchBlogs();
    } catch (error) {
      alert('Error deleting blog');
    } finally {
      setBusyPostId(null);
    }
  };

  const handleSavePost = async (updatedData) => {
    try {
      if (updatedData._id) {
        await blogService.update(updatedData._id, updatedData);
      } else {
        await blogService.create(updatedData);
      }
      fetchBlogs();
      exitEditor();
    } catch (error) {
      alert(error.response?.data?.message || 'Error saving blog');
    }
  };

  const exitEditor = () => {
    setActiveView('blog-list');
    setEditingPost(null);
  };

  const filteredBlogs = blogs
    .filter(post => {
      if (!isAdmin && post.status === 'draft') return false;

      const matchesTag = selectedTag === 'All' || (post.tags || []).some(t => t.toLowerCase() === selectedTag.toLowerCase());

      const lowerSearch = searchQuery.toLowerCase();
      const matchesSearch = 
        (post.title || '').toLowerCase().includes(lowerSearch) ||
        (post.subtitle || '').toLowerCase().includes(lowerSearch) ||
        (post.excerpt || '').toLowerCase().includes(lowerSearch) ||
        (post.tags || []).some(t => t.toLowerCase().includes(lowerSearch));

      return matchesTag && matchesSearch;
    })
    .sort((a, b) => {
      if (sortBy === 'newest') {
        return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      }
      if (sortBy === 'oldest') {
        return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
      }
      if (sortBy === 'alphabetical') {
        return (a.title || '').localeCompare(b.title || '');
      }
      return 0;
    });

  return (
    <div className="min-h-[80vh] flex flex-col bg-brand-cream-light font-sans text-gray-700 selection:bg-brand-gold/20 selection:text-brand-green-dark pb-16 pt-16">
      <SEO
        title="Blog"
        description="Insights into land healing, energetic site readings, meaningful landscape design, and the deep relationship between people and place."
        url={`${SITE_URL}/blogs`}
      />
      <main className="flex-grow pb-16 relative">
        <AnimatePresence mode="wait">
          
          {activeView === 'blog-list' && (
            <motion.div
              key="blog-list"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.3 }}
              className="max-w-7xl mx-auto px-6 md:px-12 py-12 flex flex-col gap-10"
            >
              <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 border-b border-gray-100 pb-6">
                <div>
                  <h2 className="font-serif text-4xl md:text-5xl text-brand-green-dark font-medium leading-none tracking-tight">
                    Our Blog
                  </h2>
                  <p className="text-sm text-gray-400 mt-2 font-light max-w-xl">
                    Insights into land healing, energetic site readings, meaningful landscape design, and the deep relationship between people and place.
                  </p>
                </div>
                
                <div className="flex items-center gap-4">
                  {isAdmin && (
                    <button 
                      onClick={handleNewPost}
                      className="flex items-center gap-2 bg-brand-green-dark text-white px-4 py-2 rounded-md hover:bg-brand-green-medium transition-colors text-sm font-medium"
                    >
                      <Plus size={16} /> New Post
                    </button>
                  )}
                  
                  <button 
                    onClick={handleLoginToggle}
                    className="flex items-center gap-2 border border-brand-green-dark text-brand-green-dark px-4 py-2 rounded-md hover:bg-brand-green-dark/10 transition-colors text-sm font-medium"
                  >
                    {isAdmin ? (
                      <><LogOut size={16} /> Logout</>
                    ) : (
                      <><Lock size={16} /> Admin Login</>
                    )}
                  </button>
                </div>
              </div>

              {isAdmin && (
                <div className="bg-brand-green-light/10 border border-brand-green-light/25 py-2 px-4 rounded-lg text-sm text-brand-green-dark font-semibold flex items-center gap-2 shadow-sm">
                  <BookOpen className="w-4 h-4 text-brand-green-medium" />
                  Authorized Editor Mode Active
                </div>
              )}

              <div className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm flex flex-col md:flex-row gap-4 items-center justify-between" id="search-bar-container">
                <div className="flex items-center gap-2 overflow-x-auto w-full md:w-auto pb-2 md:pb-0 scrollbar-none">
                  <Filter className="w-4 h-4 text-brand-gold/70 shrink-0 hidden sm:block" />
                  <div className="flex gap-1.5">
                    {allDynamicTags.map((tag) => {
                      const isActive = selectedTag === tag;
                      return (
                        <button
                          key={tag}
                          onClick={() => setSelectedTag(tag)}
                          className={`text-xs font-semibold px-3.5 py-1.5 rounded-xl border tracking-wide uppercase cursor-pointer shrink-0 transition-all ${
                            isActive
                              ? 'bg-brand-green-dark border-brand-green-dark text-white font-bold'
                              : 'bg-brand-cream-light/40 border-gray-100 text-gray-500 hover:text-brand-green-dark hover:border-brand-gold/30'
                          }`}
                        >
                          {tag}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="flex items-center gap-3 w-full md:w-auto shrink-0">
                  <div className="relative w-full sm:w-60">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Search articles..."
                      className="w-full pl-9 pr-4 py-2 rounded-xl text-xs border border-gray-200 focus:outline-none focus:border-brand-gold bg-brand-cream-light/30 transition-all text-gray-700 font-medium"
                    />
                  </div>

                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="py-2 px-3 rounded-xl text-xs border border-gray-200 bg-brand-cream-light/30 focus:outline-none focus:border-brand-gold font-semibold text-gray-500 cursor-pointer"
                  >
                    <option value="newest">Newest</option>
                    <option value="oldest">Oldest</option>
                    <option value="alphabetical">Title A-Z</option>
                  </select>
                </div>
              </div>

              {isLoading ? (
                <div className="flex flex-col items-center justify-center py-24 gap-4 text-brand-green-medium">
                  <Loader2 className="w-8 h-8 animate-spin" />
                  <p className="text-xs text-gray-400 font-light tracking-wide">Loading articles…</p>
                </div>
              ) : filteredBlogs.length > 0 ? (
                <motion.div
                  layout
                  className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
                >
                  <AnimatePresence mode="popLayout">
                    {filteredBlogs.map((post) => (
                      <BlogCard
                        key={post._id}
                        post={post}
                        isAdmin={isAdmin}
                        isBusy={busyPostId === post._id}
                        onEdit={handleEditPost}
                        onDelete={handleDeletePost}
                      />
                    ))}
                  </AnimatePresence>
                </motion.div>
              ) : (
                <div className="text-center py-20 bg-white rounded-2xl border border-gray-100 shadow-sm flex flex-col items-center justify-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-brand-cream flex items-center justify-center text-brand-gold">
                    <Search className="w-6 h-6" />
                  </div>
                  <h3 className="font-serif text-xl text-brand-green-dark font-semibold">No Articles Found</h3>
                  <p className="text-xs text-gray-400 max-w-xs font-light">
                    No articles match your search or category filter. Try clearing the filters or write a new article.
                  </p>
                  <button
                    onClick={() => {
                      setSearchQuery('');
                      setSelectedTag('All');
                    }}
                    className="mt-2 text-xs font-bold text-brand-green-dark hover:text-brand-gold uppercase tracking-wider border-b-2 border-brand-green-dark hover:border-brand-gold transition-all"
                  >
                    Reset Filters
                  </button>
                </div>
              )}
            </motion.div>
          )}

          {activeView === 'editor' && (
            <BlogEditor
              key="editor"
              post={editingPost}
              onSave={handleSavePost}
              onCancel={exitEditor}
            />
          )}

        </AnimatePresence>
      </main>

      <AuthModal
        isOpen={isAuthModalOpen}
        onClose={() => setIsAuthModalOpen(false)}
        onLoginSuccess={handleLoginSuccess}
      />
    </div>
  );
}
